import { defineStore } from 'pinia';
import { ConversationItemTypeEnum, ConversationStatusType, MessageInboundState } from "@/types/ConversationTypes";
// eslint-disable-next-line no-unused-vars
import ParentService from "../services/parent.service";
import SoundService, { Sounds } from "../services/sound.service";
import DomUtil from '@/utils/dom.util';
import { i18n, logger } from '../main';
import { AuthorType } from "@/types/UserTypes";
import ConversationService from "@/services/conversation.service";
// import {t} from "@/utils/lang.util";
import { delay } from "@/utils/index";
import { Views } from "@/types/ConfigTypes";
import API from "@/services/api";
import { ElementDataType, ElementType, FormTypes } from "@/types/FormTypes";
import debounce from 'lodash/debounce';
export const useConversationStore = defineStore('conversation', {
    state: () => {
        return {
            conversations: {
                unreads: 0,
                items: []
            },
            loaded: false,
            archivedCount: null,
            listItemsLoaded: false,
            currentConversationIndex: -1,
            currentConversationId: null,
        };
    },
    getters: {
        getConversations: (state) => state.conversations.items,
        getCurrentConversationId: (state) => state.currentConversationId,
        getCurrentConversation: (state) => {
            if (state.conversations.items)
                return state.conversations.items[state.currentConversationIndex];
        },
        isComposePanelDisabled: (state) => {
            return state.conversations.items[state.currentConversationIndex]?.disableComposePanel
                || state.conversations.items[state.currentConversationIndex]?.status === ConversationStatusType.CLOSED;
        },
        isScrolledToBottom: (state) => {
            return state.conversations.items[state.currentConversationIndex]?.scrolledToBottom;
        },
        getLastConversationId: (state) => {
            return state.conversations.items.length ? state.conversations.items[state.conversations.items.length - 1].id : null;
        },
        isConversationFocused: (state) => state.conversations?.items[state.currentConversationIndex]?.focused || null,
        getUnreadMsgCount: (state) => state.conversations?.items[state.currentConversationIndex].unreads,
        hasConversationItems: (state) => state.conversations?.items[state.currentConversationIndex]?.items?.length > 0,
        getArchivedCount: (state) => state.archivedCount,
        getTotalUnreadMsgCount: (state) => {
            if (!state.conversations.items)
                return;
            let cnt = 0;
            state.conversations.items.forEach(conversation => {
                cnt += conversation['unreads'];
            });
            return cnt;
        },
        isStoreLoaded: (state) => state.loaded,
        isListOfItemsLoaded: (state) => state.listItemsLoaded,
        canStartNewConversation: (state) => {
            const items = state.conversations.items;
            // limit on # of open chats
            if (items.length > 10)
                return;
            // only 1 (temp conversation) can be opened (prevent spamming and customer's losing track of endless (empty) conversations)
            for (let i = 0; i < items.length; i++) {
                // if caseId is null, it's a temp chat (not an actual chat yet)
                if (!items[i].caseId)
                    return false;
            }
            return true;
        },
        // isFirstVisitorMessageToSend: (state) => {
        //     return state.conversations?.items[state.currentConversationIndex].items
        //         .filter((item: ConversationItem) => item.authorType === AuthorType.CUSTOMER).length === 1
        // }
    },
    // could also be defined as
    // state: () => ({ count: 0 })
    actions: {
        setConversations(items) {
            // @ts-ignore
            if (!items)
                return;
            this.conversations.items = items;
            this.conversations.items.forEach(conversation => {
                conversation.new = false;
                conversation.unreads = conversation.unreads || 0;
                conversation.lastSeen = conversation.lastSeen || null;
                conversation.focused = conversation.focused || false;
            });
            this.loaded = true;
        },
        setArchivedCount(cnt) {
            this.archivedCount = cnt;
        },
        getConversation(id) {
            const index = this._getIndex(id);
            if (index > -1) {
                return this.conversations.items[index];
            }
            else {
                return null;
            }
        },
        getLastMessage(id) {
            const index = this._getIndex(id);
            if (index > -1) {
                const conversation = this.conversations.items[index];
                return conversation.items[conversation.items.length - 1];
            }
            else {
                return;
            }
        },
        getMessageById(id, msgId) {
            const index = this._getIndex(id);
            if (index > -1) {
                const conversation = this.conversations.items[index];
                for (let i = 0; i < conversation.items.length; i++) {
                    if (msgId === conversation.items[i].id)
                        return conversation.items[i];
                }
            }
            return;
        },
        addMessage(id, message) {
            // find index
            const index = this._getIndex(id);
            if (index === -1)
                return;
            message = ConversationService.transformMessage(id, message);
            if (!message.body && !message.media) {
                logger.error('cannot add message: message.body or message.media empty');
                return;
            }
            // add to conversation
            const convItem = this.conversations.items[index];
            convItem.new = false; // set flag
            convItem.items = convItem.items || [];
            convItem.items.push(message);
            // unread logic
            if (convItem.focused && ParentService.isMessengerOpened()) {
                convItem.unreads = 0;
            }
            else {
                convItem.unreads++;
            }
            if (convItem.focused) {
                DomUtil.scrollCurrentConversationToBottom();
            }
            if (message.authorType !== AuthorType.CUSTOMER) {
                // (
                //     ! ParentService.isWindowFocused() ||
                //     (ParentService.isWindowFocused() && ! ParentService.isMessengerOpened()))
                // ) {
                message.lastMessageReceived = Date.now() / 1000;
                SoundService.play(Sounds.NEW_MESSAGE);
                const body = message.body && message.body !== '' ? message.body : '-';
                // @ts-ignore
                ParentService.setDocumentTitle(i18n.global.t('common.page_title', { message: body }), 5000);
            }
            // reset agent is typing notification
            if (message.authorType === AuthorType.AGENT) {
                convItem.agent.isTyping = false;
            }
            // update unreads counter on launcher icon
            ParentService.updateUnreadsCount(this.getTotalUnreadMsgCount);
            // const resp = ParentService.updateUnreadsCount(this.getTotalUnreadMsgCount)
            // if(resp?.message?.type === 'show-floating-conversation') {
            //     CoreService.setToFloatingConversation(id)
            // }
        },
        async addBotMessage(id, message) {
            const index = this._getIndex(id);
            if (index > -1) {
                const msg = this._createBotMessage(message);
                const length = this.conversations.items[index].items.push(msg);
                await delay(500);
                this.conversations.items[index].items[length - 1].status = MessageInboundState.RECEIVED;
            }
        },
        async addContactFormMessage(id) {
            const index = this._getIndex(id);
            if (index > -1) {
                const msg = this._createContactFormMessage();
                const length = this.conversations.items[index].items.push(msg);
                await delay(500);
                this.conversations.items[index].items[length - 1].status = MessageInboundState.RECEIVED;
            }
        },
        async setConversation(id, newItem) {
            const index = this._getIndex(id);
            let prechatFound = false;
            if (index > -1) {
                this.currentConversationIndex = index;
                newItem.unreads = 0;
                newItem.lastSeen = Date.now() / 1000;
                newItem.focused = true;
                newItem.new = false;
                // bug in backend. conversation.agent.status = null
                if (!newItem.agent.status) {
                    // keep the old status from store
                    newItem.agent.status = this.conversations.items[index].agent.status;
                }
                if (newItem.items) {
                    for (let i = 0; i < newItem.items.length; i++) {
                        if (newItem.items[i].options && newItem.items[i].options['form']['type'] === FormTypes.PRECHAT) {
                            prechatFound = true;
                            newItem.disableComposePanel = true;
                        }
                    }
                }
                else {
                    newItem.items = [];
                }
                this.conversations.items[index] = newItem;
            }
            else {
                // new chat (is not in store yet)
                newItem.new = true;
                // check if prechat form is found
                if (newItem['items']) {
                    for (let i = 0; i < newItem.items.length; i++) {
                        if (newItem.items[i].options && newItem.items[i].options['form']['type'] === FormTypes.PRECHAT) {
                            prechatFound = true;
                            newItem.disableComposePanel = true;
                        }
                    }
                }
                else {
                    newItem.items = [];
                }
                this.conversations.items.push(newItem);
                // set current conversationIndex
                this.currentConversationIndex = this.conversations.items.length - 1;
                // update view
                ParentService.changeView(Views.CONVERSATION, { id: id });
            }
            // set bot message on empty conversation
            if (!prechatFound && !this.hasConversationItems) {
                // console.log(prechatFound, this.hasConversationItems)
                this.getCurrentConversation.items = [];
                // await this.addBotMessage(id, t('conversation.welcome_text'))
                // await delay(100);
                // await this.addBotMessage(id, t('conversation.enter_message_info'))
            }
        },
        setMessageStatus(id, msgId, status) {
            // set message of status
            const index = this._getIndex(id);
            if (index > -1) {
                try {
                    this.conversations.items[index].items.forEach((msg, indexItem) => {
                        if (msg.id === msgId) {
                            this.conversations.items[index].items[indexItem].status = status;
                        }
                    });
                }
                catch (e) {
                    console.log(e);
                }
            }
        },
        deleteMessage(id) {
            const index = this._getIndex(id);
            if (index > -1) {
                try {
                    this.conversations.items[index].items.splice(index, 1);
                }
                catch (e) {
                    console.log(e);
                }
            }
        },
        setMessageId(id, tempMsgId, newMsgId) {
            // set message of status
            const index = this._getIndex(id);
            if (index > -1) {
                try {
                    this.conversations.items[index].items.forEach((msg, indexItem) => {
                        if (msg.id === tempMsgId) {
                            this.conversations.items[index].items[indexItem].id = newMsgId;
                        }
                    });
                }
                catch (e) {
                    console.log(e);
                }
            }
        },
        update(id, updates) {
            const index = this._getIndex(id);
            if (index > -1) {
                const conversation = this.conversations.items[index];
                if (updates.agent) {
                    for (const [key, value] of Object.entries(updates.agent)) {
                        conversation.agent[key] = value;
                    }
                    if (updates.agent.isTyping) {
                        this._debounceTyping(conversation);
                    }
                    // conversation.agent = updates.agent
                }
            }
        },
        close(id) {
            const index = this._getIndex(id);
            if (index > -1) {
                const conversation = this.conversations.items[index];
                conversation.disableComposePanel = true;
                conversation['status'] = ConversationStatusType.CLOSED;
                DomUtil.scrollCurrentConversationToBottom();
            }
        },
        updateMessage(id, msgUpdate) {
            const index = this._getIndex(id);
            if (index > -1) {
                const conversation = this.conversations.items[index];
                const messages = conversation.items;
                const msg = messages.filter((msg) => msgUpdate.id === msg.id);
                // status update
                if (msg.length > 0 && msg[0].status) {
                    msg[0].status = msgUpdate.status;
                }
            }
        },
        setDisableComposePanel(b) {
            const index = this._getIndex(this.currentConversationId);
            if (index > -1) {
                this.conversations.items[index].disableComposePanel = b;
            }
        },
        /** open conversation (viewed by user)
         * call this method to let store know if the specific conversation is opened and viewed by user (needed to determine any unread messages)
         **/
        focus(id) {
            if (!id)
                return;
            const index = this._getIndex(id);
            if (index > -1) {
                this.currentConversationId = id;
                // const prevUnreads = this.conversations.items[index].unreads
                // if(index > -1 && ParentService.isMessengerOpened()) {
                const now = Date.now() - 1000;
                this.conversations.items[index].lastSeen = now;
                this.conversations.items[index].unreads = 0;
                this.conversations.items[index].focused = true;
                // send update all messages read
                // ConversationService.sendLastMessageSeen(id, now).then()
                // prevent propagating totalunreadmessage count multiple times when value is still 0
                // console.log(this.getTotalUnreadMsgCount, '<--')
                // if (prevUnreads !== this.getTotalUnreadMsgCount) {
                ParentService.updateUnreadsCount(this.getTotalUnreadMsgCount);
                // }
            }
        },
        scrolledToBottom(id, b = true) {
            const index = this._getIndex(id);
            if (index > -1) {
                this.conversations.items[index].scrolledToBottom = b;
            }
        },
        /** close conversation (no longer viewed by user) */
        blur(id) {
            if (!id)
                return;
            const index = this._getIndex(id);
            if (index > -1 && ParentService.isMessengerOpened()) {
                this.conversations.items[index].lastSeen = Date.now() - 1000;
                this.conversations.items[index].unreads = 0;
                this.conversations.items[index].focused = false;
                if (this.conversations.items[index].unreads !== this.getTotalUnreadMsgCount) {
                    ParentService.updateUnreadsCount(this.getTotalUnreadMsgCount);
                }
            }
            // logger.debug('blur conversation:', id, this.conversations.items[index].focused, index);
        },
        /** set attachment url */
        setUrl(id, msgId, attachmentIndex, url) {
            if (!id)
                return;
            const index = this._getIndex(id);
            if (index > -1) {
                // const conversation = this.conversations.items[index]
                // const messages = conversation.items
                const msg = this.getMessageById(id, msgId);
                // const msg = messages.filter((msg) => {
                //     console.log(msgId, msg.id)
                // return msgId == msg.id
                // })
                // console.log(attachmentIndex, msg[0].media[attachmentIndex])
                msg.media[attachmentIndex].content.url = url;
            }
        },
        /** alias of focus function */
        setLastSeen(id) {
            this.focus(id);
        },
        _createContactFormMessage() {
            return {
                id: null,
                type: ConversationItemTypeEnum.FORM,
                authorType: AuthorType.BOT,
                author: {},
                body: '',
                options: {
                    form: {
                        apiEndpoint: `${API.FORMS}`,
                        type: FormTypes.CONTACT_DETAILS,
                        borderedFields: true,
                        elements: [
                            {
                                type: ElementType.INPUT_TEXT,
                                dataType: ElementDataType.EMAIL,
                                name: '_CONTACT_EMAIL',
                                minLength: 3,
                                maxLength: 320,
                                required: true,
                            },
                            {
                                type: ElementType.SUBMIT,
                            },
                            {
                                type: ElementType.DISMISS,
                            }
                        ]
                    }
                },
                status: MessageInboundState.TYPING,
                ts: Math.floor(Date.now() / 1000)
            };
        },
        _createBotMessage(body) {
            return {
                id: null,
                type: ConversationItemTypeEnum.MESSAGE,
                authorType: AuthorType.BOT,
                author: {},
                body: body,
                status: MessageInboundState.TYPING,
                ts: Math.floor(Date.now() / 1000)
            };
        },
        _getIndex(id) {
            if (!this.conversations)
                return;
            return this.conversations.items.findIndex(obj => {
                // @ts-ignore
                return obj.id === parseInt(id);
            });
        },
        _debounceTyping: debounce((conversation) => {
            conversation.agent.isTyping = false;
        }, 5000),
    },
});
